import { Card, Col, Row, Form, Button, OverlayTrigger, Tooltip} from "react-bootstrap";
import { useEffect, useContext, useState, Fragment } from "react";
import { abpnClient } from "../../../../Services/AbpnClient";
import { AppliedToExamination } from "../Models/AppliedToExamination"
import { AuthContext } from "../../../Auth/AuthProvider";
import AbpnSpinner from "../../../Shared/Components/AbpnSpinner";
import { Link } from "react-router-dom";
import { CandidateStatus, KnownCandidateStatusReason } from "../../../Apply/Models/ApplicationModel";
import { ExamineeStatus, ExamineeStatusReason } from "../Models/AppliedToExamination";
import { deepCopy } from "../../../Shared/Utility/DeepCopy";
import { getDateWithOffset } from "../../../Shared/Utility/GetDateWithOffset";
import { ArticleReentryExaminationCard } from "./ArticleReentryExaminationCard";
import { ArticleExaminationCard } from "./ArticleExaminationCard";

export function ExaminationsCard() {
    const [examsAppliedTo, setExamsAppliedTo] = useState<Array<AppliedToExamination>>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        const getExams = () => {
            abpnClient.get<Array<AppliedToExamination>>(`/api/Examination/GetAppliedToExaminations`, authContext)
                .then(exams => {
                    setExamsAppliedTo(exams);
                    setIsLoading(false);
                });
        }
        getExams();
    }, []);

    const authContext = useContext(AuthContext);

    const getDateFormatted = (date: string) => {
        return getDateWithOffset(date).toLocaleDateString();
    }

    const getDateTimeFormatted = (date: string) => {
        return getDateWithOffset(date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    }

    const getCandidateStatusDescription = (exam: AppliedToExamination) => {
        if (exam.application.needsFinalReview) return "Approved (Pending PD Graduation Attestation & Credentials Final Review - Allow up to 4 weeks after graduation)";

        if (exam.application.candidateStatus === CandidateStatus.ToBeReviewed
            || (exam.application.candidateStatus === CandidateStatus.Approved && exam.application.candidateStatusReason.length > 0)) {
                return "Application Pending Review by Credentials Department (allow up to 16 weeks)";
            } 

        if (exam.application.candidateStatus === CandidateStatus.Withdrawn 
            || (exam.application.candidateStatus === CandidateStatus.Denied && exam.application.knownCandidateStatusReason === KnownCandidateStatusReason.Withdrew)) {
                return "Application Withdrew";
            } 

        if (exam.application.candidateStatus === CandidateStatus.Unsubmitted){
            return exam.application.candidateStatusDescription + getApplicationDeadlineDate(exam)
        }

        return exam.application.candidateStatusDescription;
    }

    const getCandidateStatusReason = (exam: AppliedToExamination) => {
        if (exam.application.needsFinalReview) return "";

        if (exam.application.candidateStatusReason?.length === 0 
            || exam.application.candidateStatus === CandidateStatus.ToBeReviewed
            || exam.application.candidateStatus === CandidateStatus.TransferredOut
            || exam.application.candidateStatus === CandidateStatus.Withdrawn
            || (exam.application.candidateStatus === CandidateStatus.Approved && exam.application.candidateStatusReason.length > 0)) return "";

        return `: ${exam.application.candidateStatusReason}`;
    }

    const getExamineeStatusDescription = (exam: AppliedToExamination) => {
        if (exam.examineeStatus === ExamineeStatus.Authorized 
            && (exam.appointments == null || exam.appointments.length === 0)) return exam.examineeStatusDescription;

        if ((exam.examineeStatus === ExamineeStatus.Delivered && exam.examineeStatusReasonId === ExamineeStatusReason.NoShow) || exam.examineeStatusReasonId === ExamineeStatusReason.Invalidated) return exam.examineeStatusReason;

        return exam.examineeStatusDescription;
    }

    const deleteApplication = (applicationId: number) => {
        const url = `/api/Application/delete?applicationId=${applicationId}`;
            abpnClient.post<number>(url, null, authContext).then(() => {
                removeDeletedApplicationExam(applicationId);
            });
    }

    const removeDeletedApplicationExam = (applicationId: number) => {
        var existing = deepCopy(examsAppliedTo);
        var updated = existing.filter(_ => _.application.applicationId !== applicationId);
        setExamsAppliedTo(updated);
    }


    const hideMakePaymentButton = (exam: AppliedToExamination) =>{
        if(exam.application.candidateStatus == CandidateStatus.Denied || exam.application.candidateStatus == CandidateStatus.Withdrawn) return false;
        if((exam.application.candidateStatus !== CandidateStatus.Unsubmitted || exam.application.isTransferredIn) && exam.order?.balance > 0) return true;
        
            return false;
    }

    const getPaymentButtonText = (exam: AppliedToExamination) => {
        if (exam.application.isTransferredIn) return "Complete and Pay"
        
        return "Make Payment";
    }

    const getExamDateTitle = (exam: AppliedToExamination) => {
        if (exam.releaseGrades) return "Date Exam Started";
        return "Exam Dates";
    }

    const getExamDates = (exam: AppliedToExamination) => {
        if (exam.releaseGrades) return getDateWithOffset(exam.startDate).toLocaleDateString();

        return `${getDateWithOffset(exam.startDate).toLocaleDateString()} - ${getDateWithOffset(exam.endDate).toLocaleDateString()}`;
    }

    const getApplicationDeadlineDate = (exam: AppliedToExamination) => {
        var date = exam.extensionDate ?? exam.applicationDeadlineDate;
        return ` (Application ${exam.hasDeadlineException ? "Exception" : ""} Deadline: ${getDateWithOffset(date).toLocaleDateString()})`;
    }

    const isGradeAvailable = (exam: AppliedToExamination) => {
        return exam.releaseGrades && exam.examinationGrade !== null && exam.examineeStatus == ExamineeStatus.GradingComplete;
    }

    const confirmDeleteApplication = (exam: AppliedToExamination, isConfirming: boolean) => {
        var updatedExams = deepCopy(examsAppliedTo);
        var updatedExam = updatedExams.find(_ => _.examinationId === exam.examinationId);
        updatedExam.isConfirmingDelete = isConfirming;
        setExamsAppliedTo(updatedExams);
    }

    if (isLoading) return <AbpnSpinner></AbpnSpinner>;

    return (
        <Card className="mb-4" border="pp-secondary-grey">
            <Card.Body>
                <Card.Title className="mb-0 font-weight-bolder">
                    <span className="text-pp-primary">Examinations</span>
                </Card.Title>
                    <Row className="mt-3">
                        <Col xs={12} md={12} lg={12} xl={12}>
                            {examsAppliedTo?.sort((a, b) => {return (a.isReEntry === b.isReEntry)? 0 : a.isReEntry? -1 : 1;}).map(exam => {    
                                return (
                                    <Fragment key={exam.examinationId}>
                                        {(exam.isArticleBased && exam.examineeStatus != ExamineeStatus.GradingComplete && exam.isReEntry && exam.examinationId == examsAppliedTo[0].examinationId) && 
                                        <ArticleReentryExaminationCard/>
                                        }
                                        <Row noGutters>
                                            <Col className="mb-1" xs={12} md={12} lg={4} xl={4}>
                                                    <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">{exam.title.toUpperCase()}</Form.Label>
                                                    <Form.Label className="font-weight-bolder mb-1 pp-font-size-smallest">{exam.moduleSelection}{exam.formattedCnpType}</Form.Label>
                                            </Col>

                                            {(exam.appointments == null || exam.appointments.length === 0 ) &&
                                            <Col className="mb-1" xs={6} md={6} lg={6} xl={4}>
                                                    <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">{getExamDateTitle(exam)}</Form.Label>
                                                    <Form.Text>{getExamDates(exam)}</Form.Text>                    
                                            </Col>}

                                             {(exam.appointments != null && exam.appointments.length > 0 && !exam.releaseGrades) &&
                                            <Col className="mb-1" xs={6} md={6} lg={8} xl={8}>
                                                <Row>
                                                    <Col>
                                                        <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller pp-side-icon">{`Exam Appointment${exam.appointments.length > 1 ? 's' : ''}`}
                                                            <OverlayTrigger
                                                                placement={"right"}
                                                                overlay={<Tooltip id="contactInfo" bsPrefix="pp-tooltip">
                                                                    Appointment time(s)
                                                                    displayed is that of the 
                                                                    Pearson VUE test center
                                                                    </Tooltip>}>
                                                                <i className="las la-info-circle ml-1"></i>
                                                            </OverlayTrigger>
                                                        </Form.Label>
                                                    </Col>
                                                </Row>
                                                <Row>

                                                {exam.appointments.map(_ => 
                                                    <Col className="mb-3" xs={12} md={12} lg={6} xl={6}>
                                                        <Form.Text className="mt-0">{`${getDateFormatted(_.startDateTime)} ${getDateTimeFormatted(_.startDateTime)}`}</Form.Text> 
                                                        <Form.Text className="mt-0"> {_.addressLine1}<br/> 
                                                        {_.addressLine2 ? <span><span>{_.addressLine2}</span><br /></span> : null} 
                                                        {_.addressLine3 ? <span><span>{_.addressLine3}</span><br /></span>  : null}
                                                        {_.city}, {_.stateCode} {_.postalCode}</Form.Text>
                                                    </Col>
                                                    )}
                                                </Row>            
                                            </Col>}

                                            {((exam.application.candidateStatus !== CandidateStatus.Unsubmitted || exam.application.isTransferredIn) && exam.order?.balance > 0) &&
                                            <Col className="mb-1" xs={6} md={6} lg={3} xl={3}>
                                                <Form.Group controlId="examBalance">
                                                    <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Balance</Form.Label>
                                                    <Form.Text>${exam.order.balance}</Form.Text>                    
                                                </Form.Group>
                                            </Col>}     

                                            {isGradeAvailable(exam) &&
                                            <Col className="mb-1" xs={6} md={6} lg={3} xl={3}>
                                                <Link to={`ExaminationHistory`}><Button variant="pp-primary" className="mr-4">View Results</Button></Link>                
                                            </Col>}     

                                        </Row>

                                        {!isGradeAvailable(exam) ? 
                                            <Row noGutters className="align-items-end mb-3">
                                                <Col xs={12} md={12} lg={4} xl={4}></Col>
                                                <Col xs={12} md={12} lg={8} xl={8}>
                                                    <hr className="mt-0 mb-1"/>
                                                    {
                                                    exam.showExamineeStatus ?
                                                    <Fragment>
                                                        <Form.Label className="font-weight-bolder mb-1 mt-0 pp-font-size-smaller">
                                                            {(exam.examineeStatus === ExamineeStatus.AuthorizationPending || exam.examineeStatus === ExamineeStatus.NA) ? "Application Status" : "Exam Status"}
                                                        </Form.Label>
                                                        <Form.Text>{`${getExamineeStatusDescription(exam)}`}</Form.Text> 
                                                    </Fragment>
                                                    : <Fragment>
                                                        <Form.Label className="font-weight-bolder mb-1 mt-0 pp-font-size-smaller">Application Status</Form.Label>
                                                        <Form.Text>{`${getCandidateStatusDescription(exam)}${getCandidateStatusReason(exam)}`}</Form.Text> 
                                                    </Fragment>}
                                                </Col>
                                            </Row> : null}
                                         {(exam.application.candidateStatus === CandidateStatus.Unsubmitted || exam.application.candidateStatus === CandidateStatus.InviteSent) 
                                            && (!exam.application.isTransferredIn || exam.order?.balance <= 0) && 
                                        <Row className="mt-3 mb-3">
                                            <Col xs={12}>
                                                <Link to={`Apply/${exam.examinationId}`}><Button variant="outline-pp-primary">Continue Application</Button></Link>
                                            </Col>                    
                                        </Row>} 
                                        {exam.application.candidateStatus === CandidateStatus.Unsubmitted && exam.order === null && !exam.isConfirmingDelete &&
                                        <Row className="mt-3 mb-3">
                                            <Col xs={12}>
                                                <Button variant="outline-pp-primary" onClick={() => confirmDeleteApplication(exam, true)}>Delete Application</Button>
                                            </Col>                    
                                        </Row>} 

                                        {exam.isConfirmingDelete &&
                                        <Row className="bg-pp-danger pt-2 pb-2">
                                            <Col>
                                                <Row>
                                                    <Col xs={12} md={12} lg={12} xl={12}>
                                                        <span className="font-weight-bolder pp-font-size-smaller mx-2">
                                                            Delete this Application?
                                                        </span>
                                                    </Col>
                                                </Row>

                                                <Row className="pt-1">
                                                    <Col xs={12}>
                                                        <span className="font-weight-bolder pp-font-size-smaller mx-2">
                                                            <Button variant="light" onClick={() => deleteApplication(exam.application.applicationId)}>Yes, Delete</Button>
                                                            <Button variant="light" className="ml-3" onClick={() => confirmDeleteApplication(exam, false)}>No, Cancel</Button>
                                                        </span>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>} 

                                        {(hideMakePaymentButton(exam)) && 
                                        <Row className="mt-3 mb-3">
                                            <Col xs={12} md={12} lg={6} xl={6}>
                                                <Link to={`Apply/${exam.examinationId}`}><Button variant="outline-pp-primary">{getPaymentButtonText(exam)}</Button></Link>
                                            </Col>                    
                                        </Row>} 

                                        {(exam.isArticleBased && !exam.isReEntry && exam.examineeId) &&
                                            <ArticleExaminationCard exam={exam}/>
                                        }
                                        
                                        <hr className="mt-0 mb-3"/>
                                    </Fragment>
                                )
                            })}
                            
                            <Row>
                                <Col xs={12}>
                                    <Link to="/AvailableExaminations"><Button variant="pp-primary" className="mr-4">Available Applications</Button></Link>
                                    {examsAppliedTo != null && <Link to={`ExaminationHistory`}><Button variant="outline-pp-primary">View Exam History</Button></Link>}
                                </Col>
                            </Row>
                        </Col>
                    </Row>
            </Card.Body>
        </Card>
    );

}